/**
 * Tracked playtests sheet (INVENTORY §2 row 7; prototype lines 1189-1213).
 *
 * The per-row `✕` is `ptUntrack`, and the distinction the footer promises is
 * real in the data layer now: an `auto` row is muted for me only and toasts
 * `MUTED FOR YOU`; anything else is removed crew-wide and toasts `UNTRACKED`
 * (PORTING-NOTES D-04, retiring INVENTORY §12 item 22).
 *
 * Which is why the two halves of that `✕` no longer cost the same. The mute is
 * mine to undo, so it stays a single tap. The crew-wide removal cascades and a
 * stray tap on it once deleted a seeded playtest for everyone
 * (docs/porting/P4b.md §7), so a non-auto row is now two taps: the first arms
 * it (`ptRmArm`) and the cell re-renders as `SURE?`, the second removes it —
 * the Crew REMOVE `rmArm` pattern, moved onto this sheet unchanged.
 */

import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { ptTrackedVM, PT_TRACKED_FOOT } from '../store/selectors.3e.ts';

const MONO = 'var(--gt-font-mono)';

export default function PtTrackedSheet(): JSX.Element {
  const s = useQd();
  const v = ptTrackedVM(s, Date.now());

  return (
    <Sheet
      label="Tracked sheet"
      maxHeight="86vh"
      hs
      onClose={s.closePtTracked}
      header={{ title: '★ TRACKED (' + v.n + ')', titleClr: 'var(--gt-text)', titleFs: '8.5px', note: '' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0 8px' }}>
        {v.rows.map((t) => (
          <div
            key={t.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 14px',
              borderBottom: '1px solid var(--gt-line-0)',
              opacity: t.dim,
            }}
          >
            <div style={{ width: 8, height: 8, flex: 'none', background: t.dot }} />
            <div
              onClick={() => {
                s.closePtTracked();
                s.openPtDet(t.id);
              }}
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 12,
                  color: 'var(--gt-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t.n}
              </div>
              <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.03em', color: t.stc }}>{t.st}</div>
              <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.03em', color: 'var(--gt-muted-2)' }}>{t.src}</div>
            </div>
            <div
              onClick={() => {
                if (t.auto || s.ptRmArm === t.id) void s.ptUntrack(t.id);
                else s.setPtRmArm(t.id);
              }}
              style={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: MONO,
                fontSize: t.xFs,
                letterSpacing: '.03em',
                color: t.xClr,
                cursor: 'pointer',
                flex: 'none',
              }}
            >
              {t.xTxt}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 14px 16px', fontFamily: MONO, fontSize: 8, lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
        {PT_TRACKED_FOOT}
      </div>
    </Sheet>
  );
}
